import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { List, Map as MapIcon, SlidersHorizontal, ArrowRight, Loader2, CarFront, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookingWidget } from "@/components/BookingWidget";
import { TaxiCard } from "@/components/TaxiCard";
import { MapView } from "@/components/MapView";
import { RequestTaxiDialog } from "@/components/RequestTaxiDialog";
import { api } from "@/lib/api";
import { loadSearch, saveOffer } from "@/lib/constants";

const SORTS = [["recommended", "Recommended"], ["price", "Lowest Fare"], ["closest", "Closest"], ["fastest", "Fastest Pickup"], ["rating", "Highest Rated"]];

export default function SearchResults() {
  const navigate = useNavigate();
  const [search, setSearch] = useState(loadSearch());
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const [sort, setSort] = useState("recommended");
  const [reqOpen, setReqOpen] = useState(false);
  const [dist, setDist] = useState(0);

  const runSearch = async (s) => {
    if (!s?.pickup) return;
    setLoading(true);
    try {
      const { data } = await api.post("/search", {
        pickup: s.pickup, dropoff: s.dropoff, service_type: s.service_type,
        vehicle_key: s.vehicle_key, date: s.date, time: s.time,
        passengers: s.passengers, luggage: s.luggage, hours: s.hours, wheelchair: s.wheelchair,
      });
      setOffers(data.offers);
      setDist(data.distance_miles);
    } catch { setOffers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { const s = loadSearch(); setSearch(s); runSearch(s); /* eslint-disable-next-line */ }, []);

  const sorted = [...offers].sort((a, b) => {
    if (sort === "price") return a.total - b.total;
    if (sort === "closest") return a.distance_from_pickup - b.distance_from_pickup;
    if (sort === "fastest") return a.pickup_eta_min - b.pickup_eta_min;
    if (sort === "rating") return b.rating - a.rating;
    return a.pickup_eta_min - b.pickup_eta_min;
  });

  const onSelect = (offer) => { saveOffer(offer); navigate("/book"); };
  const onDetails = (offer) => navigate(`/taxi/${offer.vehicle_id}`);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Journey summary bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          {search?.pickup ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-heading text-base font-semibold text-navy">{search.pickup.name}</span>
              {search.dropoff && <><ArrowRight size={16} className="text-brand" /><span className="font-heading text-base font-semibold text-navy">{search.dropoff.name}</span></>}
              <span className="ml-auto text-slate-500">{search.date} · {search.time} · {search.passengers} passenger(s){dist ? ` · ${dist} mi` : ""}</span>
            </div>
          ) : <p className="text-sm text-slate-500">Start a search to see available taxis.</p>}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6"><BookingWidget compact /></div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-navy">
            {loading ? "Searching…" : `${sorted.length} taxis available nearby`}
          </h2>
          <div className="flex items-center gap-2">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-10 w-44" data-testid="sort-select"><SlidersHorizontal size={15} /><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex rounded-lg border border-slate-200 bg-white p-1">
              <button onClick={() => setView("list")} data-testid="view-list" className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${view === "list" ? "bg-navy text-white" : "text-slate-500"}`}><List size={15} /> List</button>
              <button onClick={() => setView("map")} data-testid="view-map" className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${view === "map" ? "bg-navy text-white" : "text-slate-500"}`}><MapIcon size={15} /> Map</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>
        ) : sorted.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <CarFront size={40} className="mx-auto text-slate-300" />
            <h3 className="mt-4 font-heading text-lg font-semibold text-navy">No taxis currently match your exact search</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Try a different pickup time, nearby area or vehicle type — or let us source a taxi for your journey.</p>
            <Button className="mt-5 gap-2 bg-brand hover:bg-brand/90" onClick={() => setReqOpen(true)} data-testid="request-taxi-btn"><PhoneCall size={16} /> Request a Taxi</Button>
          </div>
        ) : view === "list" ? (
          <div className="grid gap-4">
            {sorted.map((o) => <TaxiCard key={o.offer_id} offer={o} onSelect={onSelect} onDetails={onDetails} />)}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-3"><MapView pickup={search?.pickup} dropoff={search?.dropoff} offers={sorted} onMarker={onDetails} /></div>
            <div className="lg:col-span-2 grid gap-3 max-h-[600px] overflow-auto no-scrollbar">
              {sorted.map((o) => (
                <div key={o.offer_id} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-sm font-semibold text-navy">{o.title}</span>
                    <span className="font-bold text-navy">£{o.total.toFixed(2)}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{o.operator_name} · {o.pickup_eta_min} min away</p>
                  <Button size="sm" className="mt-3 w-full bg-brand hover:bg-brand/90" onClick={() => onSelect(o)} data-testid={`map-select-${o.offer_id}`}>Select</Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <RequestTaxiDialog open={reqOpen} onOpenChange={setReqOpen} search={search} />
    </div>
  );
}
